import { useQuery } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { handle404AsNull } from '../utils';

export function useAccountProfileQuery(params: { impersonationContext?: Api.IImpersonationContext }) {
	const userSession = useUserSession();
	return useQuery<Api.AccountProfile | null>({
		queryFn: handle404AsNull(async () => {
			return userSession.webServiceHelper.callAsync<Api.AccountProfile>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `accountProfile`,
					impersonationContext: params.impersonationContext,
				}),
				'GET'
			);
		}),
		queryKey: [`accountProfile`, params.impersonationContext],
		// Fix for #17636: Disable retries for 404 responses (which are handled gracefully by returning null)
		// but keep retry protection for real errors like network failures and 500s
		retry: (failureCount, error) => {
			// Don't retry 404s - account profile doesn't exist yet
			if (typeof error === 'object' && error != null && 'systemCode' in error && error.systemCode === 404) {
				return false;
			}
			// Allow retries for other errors (network failures, 500s, etc.)
			return failureCount < 3;
		},
	});
}

export function invalidateAccountProfileQueries(params: { impersonationContext?: Api.IImpersonationContext }) {
	return reactQueryClient.invalidateQueries({
		queryKey: [`accountProfile`, params.impersonationContext],
	});
}
